/**
 * ConductorAI — pure behaviour tree for "The Conductor" Stage 4 boss.
 * No Phaser imports — fully testable in Node.js.
 *
 * Tube conductor with an electric baton. Attack patterns:
 *   baton_stun_jab          — close-range jab that briefly stuns the player
 *   third_rail_electrify    — telegraphed floor-zone that damages over a window
 *   third_rail_electrify_wide — phase-2 variant with a wider zone
 *
 * Phase 1 (HP > 30%): round-robin baton_stun_jab → third_rail_electrify
 * Phase 2 (HP ≤ 30%): same cycle, rail zone widens (third_rail_electrify_wide)
 *
 * Summon: at 60% HP (once only) — add-wave of track-rat-handler minions.
 */
import { type EnemyAIInput, ENGAGE_RANGE } from '@/systems/EnemyAI';

// ── Range constants ───────────────────────────────────────────────────────────
/** Baton jab requires close quarters. */
export const BATON_RANGE = 90;

// ── Timing constants ─────────────────────────────────────────────────────────
/** Short telegraph — conductor raises the baton before jabbing. */
export const BATON_WINDUP_FRAMES = 12;
/** Longer telegraph — sparks crawl along the rail before the zone activates. */
export const THIRD_RAIL_WINDUP_FRAMES = 28;
/** Retreat frames after an attack. */
export const BACKSTEP_FRAMES = 24;
/** Frames conductor blows the whistle while summoning handlers. */
export const SUMMON_FRAMES = 30;
/** Speed multiplier during backstep. */
export const CONDUCTOR_BACKSTEP_SPEED = 1.2;
const BASE_COOLDOWN = 75;

// ── HP thresholds ─────────────────────────────────────────────────────────────
/** HP fraction at which the track-rat-handler add-wave is summoned (once). */
export const SUMMON_HP_THRESHOLD = 0.6;
/** HP fraction at which phase 2 (wider rail zone) activates. */
export const PHASE_2_THRESHOLD = 0.3;

// ── Types ─────────────────────────────────────────────────────────────────────
export type ConductorPhase = 1 | 2;

export type ConductorAttack =
  | 'baton_stun_jab'
  | 'third_rail_electrify'
  | 'third_rail_electrify_wide';

export type ConductorNode =
  | 'scan'
  | 'approach'
  | 'baton_windup'
  | 'third_rail_windup'
  | 'summoning'
  | 'backstep';

export interface ConductorAIState {
  node: ConductorNode;
  phase: ConductorPhase;
  cooldown: number;
  windup: number;
  backstepFrames: number;
  summonFrames: number;
  /** Round-robin: 0 = baton_stun_jab, 1 = third_rail_electrify. */
  attackCycle: number;
  /** True once the handler summon has been triggered; prevents re-trigger. */
  handlersSummoned: boolean;
}

export interface ConductorAIInput extends EnemyAIInput {
  /** Current HP ÷ maxHp (0..1). */
  hpFraction: number;
}

export interface ConductorAIOutput {
  moveX: number;
  moveY: number;
  facing: 1 | -1;
  wantsAttack: boolean;
  attackType: ConductorAttack | null;
  /** True on the single frame the adapter should spawn the handler add-wave. */
  wantsSummonHandlers: boolean;
  nextState: ConductorAIState;
}

export function initialConductorState(): ConductorAIState {
  return {
    node: 'scan',
    phase: 1,
    cooldown: 0,
    windup: 0,
    backstepFrames: 0,
    summonFrames: 0,
    attackCycle: 0,
    handlersSummoned: false,
  };
}

/**
 * Advance the Conductor behaviour tree by one frame.
 * Pure function — the Phaser adapter applies movement and spawns hitboxes /
 * handler summons based on the output.
 */
export function tickConductorAI(
  input: ConductorAIInput,
  state: ConductorAIState,
): ConductorAIOutput {
  const facing: 1 | -1 = input.targetX >= input.selfX ? 1 : -1;

  if (input.isKO || input.isBeingThrown) {
    return noOp(facing, initialConductorState());
  }

  const phase: ConductorPhase = input.hpFraction <= PHASE_2_THRESHOLD ? 2 : 1;
  const { handlersSummoned } = state;

  // ── Summon gate: one-time trigger at SUMMON_HP_THRESHOLD ──────────────────
  if (!handlersSummoned && input.hpFraction <= SUMMON_HP_THRESHOLD && state.node !== 'summoning') {
    return noOp(facing, {
      ...state,
      node: 'summoning',
      phase,
      handlersSummoned: true,
      cooldown: 0,
      windup: 0,
      backstepFrames: 0,
      summonFrames: SUMMON_FRAMES,
    });
  }

  // ── Tick counters ─────────────────────────────────────────────────────────
  const cooldown = Math.max(0, state.cooldown - 1);
  const windup = Math.max(0, state.windup - 1);
  const backstepFrames = Math.max(0, state.backstepFrames - 1);
  const summonFrames = Math.max(0, state.summonFrames - 1);

  // ── Hitstun: freeze animation, keep ticking cooldown ─────────────────────
  if (input.isHitstunned) {
    return noOp(facing, {
      ...state,
      phase,
      handlersSummoned,
      node: 'scan',
      cooldown,
      windup: 0,
      backstepFrames: 0,
      summonFrames: 0,
    });
  }

  // ── SUMMONING ─────────────────────────────────────────────────────────────
  if (state.node === 'summoning') {
    if (summonFrames > 0) {
      return noOp(facing, { ...state, phase, handlersSummoned, cooldown, summonFrames });
    }
    // Animation complete — fire the one-time spawn signal
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: false,
      attackType: null,
      wantsSummonHandlers: true,
      nextState: {
        ...state,
        node: 'scan',
        phase,
        handlersSummoned,
        cooldown: cooldownFor(input.aggressionScale),
        summonFrames: 0,
      },
    };
  }

  // ── BACKSTEP ──────────────────────────────────────────────────────────────
  if (state.node === 'backstep') {
    if (backstepFrames > 0) {
      const { dx, dy, dist } = toTarget(input);
      const away = norm(-dx, -dy, dist);
      return {
        moveX: away.x * CONDUCTOR_BACKSTEP_SPEED,
        moveY: away.y * CONDUCTOR_BACKSTEP_SPEED,
        facing,
        wantsAttack: false,
        attackType: null,
        wantsSummonHandlers: false,
        nextState: { ...state, phase, handlersSummoned, cooldown, backstepFrames },
      };
    }
    return noOp(facing, {
      ...state,
      phase,
      handlersSummoned,
      cooldown,
      node: 'scan',
      backstepFrames: 0,
    });
  }

  // ── BATON WINDUP ──────────────────────────────────────────────────────────
  if (state.node === 'baton_windup') {
    if (windup > 0) return noOp(facing, { ...state, phase, handlersSummoned, cooldown, windup });
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      attackType: 'baton_stun_jab',
      wantsSummonHandlers: false,
      nextState: {
        ...state,
        phase,
        handlersSummoned,
        node: 'backstep',
        cooldown: cooldownFor(input.aggressionScale),
        windup: 0,
        backstepFrames: BACKSTEP_FRAMES,
        attackCycle: nextCycle(state.attackCycle),
      },
    };
  }

  // ── THIRD RAIL WINDUP ─────────────────────────────────────────────────────
  if (state.node === 'third_rail_windup') {
    if (windup > 0) return noOp(facing, { ...state, phase, handlersSummoned, cooldown, windup });
    const attackType: ConductorAttack =
      phase >= 2 ? 'third_rail_electrify_wide' : 'third_rail_electrify';
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      attackType,
      wantsSummonHandlers: false,
      nextState: {
        ...state,
        phase,
        handlersSummoned,
        node: 'backstep',
        cooldown: cooldownFor(input.aggressionScale),
        windup: 0,
        backstepFrames: BACKSTEP_FRAMES,
        attackCycle: nextCycle(state.attackCycle),
      },
    };
  }

  // ── SCAN / APPROACH → select next attack ─────────────────────────────────
  const { dist } = toTarget(input);
  const { attackCycle } = state;

  if (cooldown === 0 && input.canAttack) {
    if (attackCycle === 0) {
      // baton_stun_jab: requires being in close range
      if (dist <= BATON_RANGE) {
        return noOp(facing, {
          ...state,
          phase,
          handlersSummoned,
          cooldown,
          node: 'baton_windup',
          windup: BATON_WINDUP_FRAMES,
        });
      }
    } else {
      // attackCycle === 1: third_rail_electrify fires at any range
      return noOp(facing, {
        ...state,
        phase,
        handlersSummoned,
        cooldown,
        node: 'third_rail_windup',
        windup: THIRD_RAIL_WINDUP_FRAMES,
      });
    }
  }

  // ── Approach orbit position ───────────────────────────────────────────────
  const orbitX = input.targetX + input.orbitOffsetX;
  const orbitY = input.targetY + input.orbitOffsetY;
  const odx = orbitX - input.selfX;
  const ody = orbitY - input.selfY;
  const orbitDist = Math.hypot(odx, ody);

  if (orbitDist > 8) {
    const speedMul = 1 + input.aggressionScale * 0.3;
    const move = norm(odx, ody, orbitDist);
    const node: ConductorNode = dist > ENGAGE_RANGE ? 'scan' : 'approach';
    return {
      moveX: move.x * speedMul,
      moveY: move.y * speedMul,
      facing,
      wantsAttack: false,
      attackType: null,
      wantsSummonHandlers: false,
      nextState: {
        ...state,
        phase,
        handlersSummoned,
        cooldown,
        node,
        windup: 0,
        backstepFrames: 0,
        summonFrames: 0,
      },
    };
  }

  return noOp(facing, { ...state, phase, handlersSummoned, cooldown, node: 'scan' });
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function cooldownFor(aggressionScale: number): number {
  return Math.round(BASE_COOLDOWN * (1 - aggressionScale * 0.5));
}

function toTarget(input: ConductorAIInput): { dx: number; dy: number; dist: number } {
  const dx = input.targetX - input.selfX;
  const dy = input.targetY - input.selfY;
  return { dx, dy, dist: Math.hypot(dx, dy) };
}

function norm(x: number, y: number, len: number): { x: number; y: number } {
  const d = len || 1;
  return { x: x / d, y: y / d };
}

function noOp(facing: 1 | -1, nextState: ConductorAIState): ConductorAIOutput {
  return {
    moveX: 0,
    moveY: 0,
    facing,
    wantsAttack: false,
    attackType: null,
    wantsSummonHandlers: false,
    nextState,
  };
}

function nextCycle(c: number): number {
  return (c + 1) % 2;
}
